import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import PoolCreationModal from './PoolCreationModal';
import PoolManagementCard from './PoolManagementCard';
import TradeExecutionModal from './TradeExecutionModal';
import InvestorManagementModal from './InvestorManagementModal';
import PoolAnalyticsModal from './PoolAnalyticsModal';
import ApiRateLimitManager from './ApiRateLimitManager';
import AdminOpportunityManager from './AdminOpportunityManager';
import AdminTokenManager from './AdminTokenManager';
import TokenSubmissionManager from './TokenSubmissionManager';
import TradingConfigManager from './TradingConfigManager';
import './AdminPoolDashboard.css';

const AdminPoolDashboard = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPool, setSelectedPool] = useState(null);
  const [activeTab, setActiveTab] = useState('pools');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [showInvestorModal, setShowInvestorModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  
  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    totalPools: 0,
    totalAUM: 0,
    totalInvestors: 0,
    totalFeesCollected: 0,
    avgPerformance: 0
  });

  useEffect(() => {
    if (user && isAdmin()) {
      fetchAdminPools();
      fetchDashboardStats();
    }
  }, [user]);

  const isAdmin = () => {
    return user && (user.role === 'admin' || user.isPoolManager);
  };

  const fetchAdminPools = async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
      const response = await fetch('/api/admin/pools', {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPools(data.pools || []);
      } else {
        throw new Error('Failed to fetch admin pools');
      }
    } catch (error) {
      console.error('Error fetching admin pools:', error);
      showToast('Failed to load pools', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
      const response = await fetch('/api/admin/pools/stats', {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Ensure all numeric fields are properly parsed and have fallbacks
        setDashboardStats({
          totalPools: Number(data.stats?.totalPools) || 0,
          totalAUM: Number(data.stats?.totalAUM) || 0,
          totalInvestors: Number(data.stats?.totalInvestors) || 0,
          totalFeesCollected: Number(data.stats?.totalFeesCollected) || 0,
          avgPerformance: Number(data.stats?.avgPerformance) || 0
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const handleCreatePool = async (poolData) => {
    try {
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
      const response = await fetch('/api/admin/pools', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify(poolData)
      });

      if (response.ok) {
        const data = await response.json();
        setPools(prev => [...prev, data.pool]);
        setShowCreateModal(false);
        showToast('Pool created successfully', 'success');
        fetchDashboardStats();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create pool');
      }
    } catch (error) {
      console.error('Error creating pool:', error);
      showToast(error.message, 'error');
    }
  };

  const handleUpdatePoolStatus = async (poolId, status) => {
    try {
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
      const response = await fetch(`/api/admin/pools/${poolId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        setPools(prev => prev.map(pool => 
          pool.id === poolId ? { ...pool, status } : pool
        ));
        showToast(`Pool ${status} successfully`, 'success');
      } else {
        throw new Error('Failed to update pool status');
      }
    } catch (error) {
      console.error('Error updating pool status:', error);
      showToast(error.message, 'error');
    }
  };

  const openTradeModal = (pool) => {
    setSelectedPool(pool);
    setShowTradeModal(true);
  };

  const openInvestorModal = (pool) => {
    setSelectedPool(pool);
    setShowInvestorModal(true);
  };

  const openAnalyticsModal = (pool) => {
    setSelectedPool(pool);
    setShowAnalyticsModal(true);
  };

  if (!isAdmin()) {
    return (
      <div className="admin-access-denied">
        <h2>Access Denied</h2>
        <p>You don't have permission to access the admin dashboard.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-pool-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Admin Dashboard</h1>
          <p>Manage investment pools, system settings, and monitor performance</p>
        </div>
        {activeTab === 'pools' && (
          <button
            className="create-pool-btn"
            onClick={() => setShowCreateModal(true)}
          >
            Create New Pool
          </button>
        )}
        {activeTab === 'opportunities' && (
          <div className="tab-description">
            <p>Manage trading opportunities and market signals</p>
          </div>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === 'pools' ? 'active' : ''}`}
          onClick={() => setActiveTab('pools')}
        >
          🏦 Pool Management
        </button>
        <button
          className={`tab-button ${activeTab === 'opportunities' ? 'active' : ''}`}
          onClick={() => setActiveTab('opportunities')}
        >
          📈 Trading Opportunities
        </button>
        <button
          className={`tab-button ${activeTab === 'api-limits' ? 'active' : ''}`}
          onClick={() => setActiveTab('api-limits')}
        >
          🚦 API Rate Limits
        </button>
        <button
          className={`tab-button ${activeTab === 'system' ? 'active' : ''}`}
          onClick={() => setActiveTab('system')}
        >
          ⚙️ System Settings
        </button>
        <button
          className={`tab-button ${activeTab === 'tokens' ? 'active' : ''}`}
          onClick={() => setActiveTab('tokens')}
        >
          🪙 Token Listings
        </button>
        <button
          className={`tab-button ${activeTab === 'token-submissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('token-submissions')}
        >
          📝 Token Submissions
        </button>
      </div>

      {/* Tab Content */}
      <div className="dashboard-content">
        {activeTab === 'opportunities' && (
          <div className="opportunities-section">
            <AdminOpportunityManager />
          </div>
        )}

        {activeTab === 'pools' && (
          <>
            {/* Dashboard Stats */}
            <div className="dashboard-stats">
              <div className="stat-card">
                <div className="stat-value">{dashboardStats.totalPools}</div>
                <div className="stat-label">Total Pools</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{dashboardStats.totalAUM.toFixed(2)} SOL</div>
                <div className="stat-label">Assets Under Management</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{dashboardStats.totalInvestors}</div>
                <div className="stat-label">Total Investors</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{dashboardStats.totalFeesCollected.toFixed(3)} SOL</div>
                <div className="stat-label">Fees Collected</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{dashboardStats.avgPerformance.toFixed(2)}%</div>
                <div className="stat-label">Avg Performance (30d)</div>
              </div>
            </div>

            {/* Pools Grid */}
            <div className="pools-section">
              <h2>Your Managed Pools</h2>
              {pools.length === 0 ? (
                <div className="no-pools">
                  <p>No pools created yet. Create your first pool to get started.</p>
                </div>
              ) : (
                <div className="pools-grid">
                  {pools.map(pool => (
                    <PoolManagementCard
                      key={pool.id}
                      pool={pool}
                      onStatusChange={handleUpdatePoolStatus}
                      onExecuteTrade={() => openTradeModal(pool)}
                      onManageInvestors={() => openInvestorModal(pool)}
                      onViewAnalytics={() => openAnalyticsModal(pool)}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'api-limits' && (
          <div className="api-limits-section">
            <ApiRateLimitManager />
          </div>
        )}

        {activeTab === 'system' && (
          <div className="system-settings-section">
            <TradingConfigManager />
            <div className="coming-soon" style={{ marginTop: '24px' }}>
              <h3>🚧 Additional System Settings</h3>
              <p>More configuration options coming soon...</p>
              <ul>
                <li>Database optimization settings</li>
                <li>WebSocket connection limits</li>
                <li>Cache configuration</li>
                <li>Monitoring thresholds</li>
              </ul>
            </div>
          </div>
        )}
        {activeTab === 'tokens' && (
          <div className="tokens-section">
            <AdminTokenManager />
          </div>
        )}
        {activeTab === 'token-submissions' && (
          <div className="token-submissions-section">
            <TokenSubmissionManager />
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <PoolCreationModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreatePool}
        />
      )}

      {showTradeModal && selectedPool && (
        <TradeExecutionModal
          isOpen={showTradeModal}
          onClose={() => setShowTradeModal(false)}
          pool={selectedPool}
          onTradeExecuted={fetchAdminPools}
        />
      )}

      {showInvestorModal && selectedPool && (
        <InvestorManagementModal
          isOpen={showInvestorModal}
          onClose={() => setShowInvestorModal(false)}
          pool={selectedPool}
          onInvestorsUpdated={fetchAdminPools}
        />
      )}

      {showAnalyticsModal && selectedPool && (
        <PoolAnalyticsModal
          isOpen={showAnalyticsModal}
          onClose={() => setShowAnalyticsModal(false)}
          pool={selectedPool}
        />
      )}
    </div>
  );
};

export default AdminPoolDashboard;
